import { useRef, useState, useCallback } from 'react';
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api';

const libraries = ['places'];

// A reusable address input with Google Places Autocomplete.
// When the user selects a suggestion, onAddressSelect fires with a
// structured object (street, city, state, postal code, country, formatted
// full address) instead of just a raw string — so the parent form can
// populate multiple fields from one selection.
function AddressAutocomplete({ label, value, onChange, onAddressSelect }) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const autocompleteRef = useRef(null);
  const [inputValue, setInputValue] = useState(value || '');

  const onLoad = useCallback((autocomplete) => {
    autocompleteRef.current = autocomplete;
  }, []);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (!place || !place.address_components) return;

    // Parse Google's address_components array into named fields.
    // Google returns each component with a "types" array telling us
    // what it represents (street_number, locality, etc.) — we map
    // those types to the fields our form actually needs.
    const getComponent = (type) => {
      const component = place.address_components.find((c) => c.types.includes(type));
      return component ? component.long_name : '';
    };

    const streetNumber = getComponent('street_number');
    const route = getComponent('route');
    const city = getComponent('locality') || getComponent('sublocality') || getComponent('postal_town');
    const state = getComponent('administrative_area_level_1');
    const postalCode = getComponent('postal_code');
    const country = getComponent('country');

    const structuredAddress = {
      formatted_address: place.formatted_address,
      street: [streetNumber, route].filter(Boolean).join(' '),
      city,
      state,
      postal_code: postalCode,
      country,
      lat: place.geometry?.location?.lat(),
      lng: place.geometry?.location?.lng(),
    };

    setInputValue(place.formatted_address);
    onChange(place.formatted_address);
    onAddressSelect(structuredAddress);
  };

  if (!isLoaded) {
    return (
      <div>
        <label style={styles.label}>{label}</label>
        <input disabled placeholder="Loading maps..." style={styles.input} />
      </div>
    );
  }

  return (
    <div>
      <label style={styles.label}>{label}</label>
      <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            onChange(e.target.value);
          }}
          placeholder="Start typing an address..."
          style={styles.input}
        />
      </Autocomplete>
    </div>
  );
}

const styles = {
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
};

export default AddressAutocomplete;